import { useState } from "react";
import BaseButton from "../../../components/ui/BaseButton";
import BaseModal from "../../../components/ui/BaseModal";
import { getFileIconClass, isImage } from "../../../utils/helpers";
import {
  Box,
  ImageList,
  ImageListItem,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@emotion/react";

const DocumentLibraryModal = ({
  open,
  close,
  onSelect,
  data,
  isfetchUserWorkOrdersDocumentsLoading,
  isError,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const cols = isXs ? 2 : isSm ? 3 : 4;
  const skeletonCount = data?.length || cols * 3;

  const handleToggleSelect = (file) => {
    setSelectedFiles((prev) =>
      prev.find((f) => f.id === file.id)
        ? prev.filter((f) => f.id !== file.id)
        : [...prev, file]
    );
  };

  const handleConfirm = () => {
    onSelect(selectedFiles);
    close();
  };

  return (
    <BaseModal open={open} close={close}>
      <div className="w-[90vw] max-w-[800px] bg-white rounded-lg mx-auto">
        <div className="p-5">
          <h4 className="mb-4 text-base md:text-lg font-semibold">
            Select from Library
          </h4>

          {isfetchUserWorkOrdersDocumentsLoading ? (
            <DocumentSkeletonLoading
              cols={cols}
              skeletonCount={skeletonCount}
            />
          ) : isError ? (
            <p>Failed to load documents.</p>
          ) : data?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center text-gray-500">
              <i className="fa-regular fa-folder-open text-3xl mb-3"></i>
              <p className="text-sm italic">
                No documents found in the library.
              </p>
            </div>
          ) : (
            <ImageList
              cols={cols}
              rowHeight={120}
              gap={0}
              className="w-full max-h-[400px] min-h-[392px] overflow-y-auto"
            >
              {data?.map((file) => {
                const isSelected = selectedFiles.find((f) => f.id === file.id);

                return (
                  <ImageListItem
                    key={file.id}
                    onClick={() => handleToggleSelect(file)}
                    className={`
                    group relative w-full h-full overflow-hidden cursor-pointer 
                    transition-all duration-200 ease-in-out
                    ${
                      isSelected
                        ? "border-2 border-blue-500 bg-gray-300"
                        : "border border-gray-300 bg-gray-100"
                    }
                  `}
                  >
                    <div
                      className="file-hover-label absolute bottom-0 left-0 w-full bg-black/60 text-white text-xs px-2 py-0.5 
                      opacity-0 transition-opacity duration-200 whitespace-nowrap overflow-hidden text-ellipsis pointer-events-none 
                      group-hover:opacity-100 z-20"
                    >
                      {file.fileName}
                    </div>

                    {isImage(file.fileName) ? (
                      <img
                        src={file.url}
                        alt={file.fileName}
                        loading="lazy"
                        className={`
                          w-full h-full object-cover transition duration-200
                          ${isSelected ? "brightness-50" : ""}
                        `}
                      />
                    ) : (
                      <Box
                        className={`
                        w-full h-full flex items-center justify-center text-[36px] text-gray-500 
                        transition duration-200 z-30
                        ${isSelected ? "brightness-50" : ""}
                      `}
                      >
                        <i
                          className={`fa-solid ${getFileIconClass(
                            file.fileName
                          )}`}
                        />
                      </Box>
                    )}

                    {isSelected && (
                      <Box
                        className="absolute bottom-1.5 right-1.5 bg-[#D8EBF8] text-white rounded-full w-5 h-5 
                        flex items-center justify-center text-[12px] shadow-sm"
                      >
                        <i className="fa-solid fa-check" />
                      </Box>
                    )}
                  </ImageListItem>
                );
              })}
            </ImageList>
          )}
          <div
            className="flex flex-wrap justify-center md:justify-center items-center gap-4 bg-white py-6 px-4"
            style={{
              boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
            }}
          >
            <BaseButton
              className=" px-[28px] py-[5px] bg-light text-primary hover:text-primary"
              variant="outlined"
              color="primary"
              style={{
                marginRight: "16px",
              }}
              onClick={close}
            >
              Cancel
            </BaseButton>

            {data?.length !== 0 && (
              <BaseButton
                className="px-[12px] py-[5px] min-w-32"
                color="primary"
                onClick={handleConfirm}
                disabled={!selectedFiles.length}
              >
                Add {selectedFiles.length} File(s)
              </BaseButton>
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default DocumentLibraryModal;

const DocumentSkeletonLoading = ({ cols, skeletonCount }) => (
  <ImageList
    cols={cols}
    rowHeight={120}
    gap={8}
    className="w-full max-h-[400px] min-h-[392px] overflow-y-auto"
  >
    {Array.from({ length: skeletonCount }).map((_, index) => (
      <ImageListItem key={index}>
        <Skeleton variant="rectangular" width="100%" height={120} />
      </ImageListItem>
    ))}
  </ImageList>
);
